import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// "Try the Builder module" promo banner + explainer modal + trial bar
// (shared/_builder_promo_banner). Server endpoints under
// /builder_promo/{dismiss,switch,keep,switch_back}.
//
// Persona switches restyle the whole app (sidebar labels, nav, forms),
// so switch/switch-back persist a toast in sessionStorage and do a
// full reload — same pattern as fee_schedule#_flashAndReload. The
// wrapper div always renders, so connect() still runs on the reloaded
// page to surface that pending toast even when no banner shows.
export default class extends Controller {
  static targets = ["banner", "modal", "switchButton"]

  static values = {
    companyId: Number,
    persona: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
    this._trackShownOncePerSession()
  }

  // ── Explainer modal ───────────────────────────────────────────────

  openModal() {
    this.modalTarget.classList.remove("hidden")
    this._track("Builder Promo Opened")
  }

  hideModal() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Banner actions ────────────────────────────────────────────────

  // × on the promo bar — hides it for good (server-persisted).
  dismiss() {
    this._post("/builder_promo/dismiss")
      .then(() => this.element.remove())
      .catch(() => showToast("Something went wrong. Please try again.", "error"))
  }

  // "Switch to Builder" inside the modal.
  switchToBuilder() {
    if (this.hasSwitchButtonTarget) this.switchButtonTarget.disabled = true
    this._post("/builder_promo/switch")
      .then(() => this._flashAndReload("Welcome to the Builder module! You can switch back anytime from the bar on top."))
      .catch(() => {
        if (this.hasSwitchButtonTarget) this.switchButtonTarget.disabled = false
        showToast("Couldn't switch right now. Please try again.", "error")
      })
  }

  // "Keep Builder" on the trial bar — stays put, just retires the bar.
  keep() {
    this._post("/builder_promo/keep")
      .then(() => {
        this.element.remove()
        showToast("You're all set — Builder it is!")
      })
      .catch(() => showToast("Couldn't save that. Please try again.", "error"))
  }

  // "Switch back to Owner" on the trial bar.
  switchBack() {
    this._post("/builder_promo/switch_back")
      .then(() => this._flashAndReload("Welcome back to the Owner module."))
      .catch(() => showToast("Couldn't switch back right now. Please try again.", "error"))
  }

  // ── Private ───────────────────────────────────────────────────────

  _post(url) {
    return fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    }).then((resp) => {
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      return resp
    })
  }

  _flashAndReload(message) {
    sessionStorage.setItem("builder_promo_flash", JSON.stringify({ message, type: "success" }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("builder_promo_flash")
    if (!raw) return
    sessionStorage.removeItem("builder_promo_flash")
    try {
      const { message, type } = JSON.parse(raw)
      showToast(message, type)
    } catch {
      // Corrupt entry — already removed, nothing to show.
    }
  }

  // Impression event, once per browser session (the banner renders on
  // every page, so per-pageview would swamp the funnel).
  _trackShownOncePerSession() {
    if (!this.hasBannerTarget) return
    if (sessionStorage.getItem("builder_promo_shown")) return

    sessionStorage.setItem("builder_promo_shown", "1")
    this._track("Builder Promo Shown")
  }

  _track(event) {
    track(event, {
      company_id: this.companyIdValue,
      user_role: this.personaValue
    })
  }
}
